import { clearLocalSession } from "@/hooks/useLocalSession";
import { clearStoredPayerMerchantId } from "@/lib/payerClient";
import type { Source } from "@/types";
import { getAccessToken, signOut } from "@/lib/session";
import { publicApiClient } from "@/core/api/services/publicApiClient";
import { safeStorage } from "@/shared/lib/safeStorage";

/**
 * End the session properly.
 *
 * `POST /auth/logout` blocklists the token's `jti` on the server. Dropping the
 * token client-side alone would leave a working credential behind for anyone
 * who copied it, and it stays valid until it expires 24h later.
 *
 * Sent through the anonymous client with the bearer attached by hand: the
 * authenticated client ends the session on any 401, so a 401 from the logout
 * call itself would come straight back through here.
 */
export async function logoutUser(_email?: string, callbackUrl = "/login") {
  const token = getAccessToken();
  if (token) {
    try {
      await publicApiClient.post("/auth/logout", null, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      // Always scrub the client even when the call fails.
    }
  }
  clearLocalSession();
  clearStoredPayerMerchantId();
  safeStorage.clearSession();
  await signOut({ redirect: true, callbackUrl });
}

export const maskPhone = (phone?: string) => {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  const last4 = digits.slice(-4);
  return `••••••${last4}`;
};

export const maskEmail = (email?: string) => {
  if (!email) return "";
  const [user, domain] = email.split("@");
  if (!user || !domain) return email;
  const maskedUser = user.length <= 3 ? user[0] + "***" : user.slice(0, 2) + "***";
  return `${maskedUser}@${domain}`;
};

export const isSixDigits = (v: string) => /^\d{6}$/.test(v);

const SOURCE_LABELS: Record<Source, string> = {
  pglx: "Super Admin",
  clinic: "Clinic",
  payer: "Payer",
  employer: "Employer",
  group: "Group",
  division: "Division",
  location: "Location",
  member: "Member",
};

export const normalizeSource = (value?: string | Source): Source | undefined => {
  if (!value) return undefined;
  const candidate = value.toString().toLowerCase();
  if (candidate in SOURCE_LABELS) {
    return candidate as Source;
  }
  return undefined;
};

export const getSourceLabel = (value?: string | Source) => {
  const normalized = normalizeSource(value);
  return normalized ? SOURCE_LABELS[normalized] : undefined;
};
